/**
 * G4 — trigger eval: does an authored skill fire on the situations it is for,
 * and stay quiet otherwise?
 *
 * G1–G3 ask whether a plugin is well-formed, safe, and runnable. None of them
 * ask whether it is any *good*, and for a passive capability the whole of "good"
 * is the `description`: it is the only thing the model sees when deciding
 * whether to reach for the skill. A skill that parses, validates, and never
 * triggers is dead weight that costs context on every request.
 *
 * ## The circularity trap
 *
 * The tempting design is to generate test prompts from the description and then
 * check that the description matches them. That measures self-consistency and
 * always passes. So gold prompts are **supplied**, not generated (`eval/triggers.json`
 * in the plugin, or authored alongside it), and the negatives largely come for
 * free: a prompt whose right answer is a *different* capability is a negative for
 * this one. Discriminating against real siblings is the question that matters —
 * "does it fire" is easy, "does it fire instead of the wrong thing" is not.
 *
 * ## Reuse of the search eval harness
 *
 * §4.2 asks G4 to reuse `core/search/eval-harness.ts`. Its three *rules* are
 * adopted verbatim below and are the reason this module looks the way it does:
 * pin what you measured, record it machine-readably, and never let a degraded
 * run read like a real one. Its *machinery* could not be called: that harness
 * pins a corpus by checking out a git worktree and stamps provenance with
 * embsearch daemon state, and G4's corpus is a handful of description strings
 * with no repo and no embedder. Calling it would have meant a git worktree per
 * skill eval. The pinning here is therefore a content hash of exactly what was
 * judged — same guarantee, appropriate mechanism.
 *
 * See docs/plugin-system-architecture.md §4.2.
 */

import { createHash } from "node:crypto";
import { existsSync, readFileSync } from "node:fs";
import * as path from "node:path";
import type { CapabilityDoc } from "../../capabilities/registry.js";
import { loadSkillsFromDir } from "../../skills.js";
import type { GateFinding } from "./gates.js";
import type { NormalizedPlugin } from "./manifest.js";

/** Where a plugin declares the prompts its capabilities should (and should not) fire on. */
export const TRIGGER_GOLD_FILE = path.join("eval", "triggers.json");

export interface TriggerCase {
	prompt: string;
	/** Capability name this prompt should select, or null when nothing should fire. */
	expect: string | null;
}

/** One capability offered to the judge as a candidate. */
export interface TriggerCandidate {
	name: string;
	description: string;
	/** False for the distractors drawn from other plugins. */
	own: boolean;
}

export interface TriggerJudgeRequest {
	candidates: TriggerCandidate[];
	prompts: string[];
}

/** The judge's answer per prompt: the selected candidate name, or null for none. */
export type TriggerJudgeVerdict = string | null;

/**
 * The single model call, injected.
 *
 * Injected rather than imported so the scoring, the gold-set handling and the
 * gate mapping are testable without a model — and so a caller with no model
 * configured gets an honest `not-run` instead of a gate that quietly passes.
 */
export type TriggerJudge = (request: TriggerJudgeRequest) => Promise<TriggerJudgeVerdict[]>;

export interface TriggerCaseResult {
	prompt: string;
	expected: string | null;
	actual: string | null;
	correct: boolean;
}

export interface TriggerEvalRecord {
	/** Content hash of the candidates and cases judged — the pin. */
	corpusHash: string;
	timestampMs: number;
	pluginId: string;
	candidateCount: number;
	caseCount: number;
	/** Fires when it should: TP / (TP + FN). Undefined when there were no positives. */
	recall?: number;
	/** Quiet when it should be: TN / (TN + FP). Undefined when there were no negatives. */
	specificity?: number;
	results: TriggerCaseResult[];
}

export type TriggerEvalOutcome = { status: "not-run"; reason: string } | { status: "ran"; record: TriggerEvalRecord };

/** Recall below this is an error: a skill that does not fire is not a capability. */
const MIN_RECALL = 0.8;
/** Specificity below this is a warning: over-firing costs context but breaks nothing. */
const MIN_SPECIFICITY = 0.75;

/** Read the plugin's declared gold set, or undefined when it has none. */
export function loadTriggerCases(root: string): TriggerCase[] | undefined {
	const file = path.join(root, TRIGGER_GOLD_FILE);
	if (!existsSync(file)) return undefined;
	try {
		const raw = JSON.parse(readFileSync(file, "utf8")) as { cases?: unknown };
		if (!Array.isArray(raw.cases)) return undefined;
		const cases = raw.cases.filter(
			(c): c is TriggerCase =>
				!!c &&
				typeof c === "object" &&
				typeof (c as TriggerCase).prompt === "string" &&
				(c as TriggerCase).prompt.trim().length > 0 &&
				((c as TriggerCase).expect === null || typeof (c as TriggerCase).expect === "string"),
		);
		return cases.length > 0 ? cases : undefined;
	} catch {
		return undefined;
	}
}

/**
 * The plugin's own passive capabilities, as judge candidates.
 *
 * Skills only. Commands are invoked by name (`/foo`) rather than selected from a
 * description, so "does the description fire" is not a question about them, and
 * scoring them would dilute the metric with cases that cannot fail.
 */
export function ownCandidates(plugin: NormalizedPlugin): TriggerCandidate[] {
	if (!plugin.skillsDir) return [];
	const { skills } = loadSkillsFromDir({ dir: plugin.skillsDir, source: plugin.id });
	return skills.map((s) => ({ name: s.name, description: s.description ?? "", own: true }));
}

/**
 * Sibling capabilities to put in front of the judge alongside the plugin's own.
 *
 * Without them the judge picks from a list of one and "does it fire" is nearly
 * free. The interesting failure is a description broad enough to win prompts
 * that belong to something else, and that only shows up against real competition.
 */
export function distractorCandidates(exclude: string, docs: readonly CapabilityDoc[]): TriggerCandidate[] {
	return docs
		.filter((d) => (d.kind === "skill" || d.kind === "mcp-tool") && d.source !== exclude)
		.map((d) => ({ name: d.name, description: d.description, own: false }));
}

/**
 * Pin: a hash of exactly what was judged.
 *
 * Over candidates *and* cases, because both move the number. A rerun after a
 * description edit is measuring something else, and a record that cannot say so
 * is the failure mode the search harness was built to prevent.
 */
export function triggerCorpusHash(candidates: readonly TriggerCandidate[], cases: readonly TriggerCase[]): string {
	const h = createHash("sha256");
	for (const c of [...candidates].sort((a, b) => a.name.localeCompare(b.name))) {
		h.update(`${c.name} ${c.description} `);
	}
	for (const c of cases) h.update(`${c.prompt} ${c.expect ?? ""} `);
	return h.digest("hex").slice(0, 16);
}

function ratio(correct: number, total: number): number | undefined {
	return total > 0 ? correct / total : undefined;
}

/**
 * Score a plugin's passive capabilities against its gold set.
 *
 * Never throws and never fabricates: no judge, no gold set, or no candidates all
 * return `not-run` with the reason. A judge that answers the wrong number of
 * prompts is also `not-run` — a partial alignment between prompts and verdicts
 * would silently score the wrong pairs.
 */
export async function runTriggerEval(
	pluginId: string,
	candidates: readonly TriggerCandidate[],
	cases: readonly TriggerCase[] | undefined,
	judge: TriggerJudge | undefined,
): Promise<TriggerEvalOutcome> {
	if (!judge) return { status: "not-run", reason: "no trigger judge configured (G4 needs a model)" };
	if (!cases || cases.length === 0) {
		return {
			status: "not-run",
			reason: `no gold set: add ${TRIGGER_GOLD_FILE} with the prompts this plugin should and should not fire on`,
		};
	}
	if (candidates.length === 0) return { status: "not-run", reason: "plugin has no passive capabilities to evaluate" };

	const prompts = cases.map((c) => c.prompt);
	let verdicts: TriggerJudgeVerdict[];
	try {
		verdicts = await judge({ candidates: [...candidates], prompts });
	} catch (error) {
		return { status: "not-run", reason: `judge failed: ${(error as Error).message}` };
	}
	if (verdicts.length !== prompts.length) {
		return {
			status: "not-run",
			reason: `judge returned ${verdicts.length} verdict(s) for ${prompts.length} prompt(s)`,
		};
	}

	const own = new Set(candidates.filter((c) => c.own).map((c) => c.name));
	let truePos = 0;
	let positives = 0;
	let trueNeg = 0;
	let negatives = 0;

	const results: TriggerCaseResult[] = cases.map((c, i) => {
		const actual = verdicts[i] ?? null;
		const correct = actual === c.expect;
		// A "positive" is a case this plugin should win. A case expecting a
		// sibling's capability is a negative *for us* — which is the discriminative
		// half, and why distractors are in the candidate list at all.
		if (c.expect !== null && own.has(c.expect)) {
			positives++;
			if (correct) truePos++;
		} else {
			negatives++;
			// Quiet means: did not claim one of ours.
			if (actual === null || !own.has(actual)) trueNeg++;
		}
		return { prompt: c.prompt, expected: c.expect, actual, correct };
	});

	return {
		status: "ran",
		record: {
			corpusHash: triggerCorpusHash(candidates, cases),
			timestampMs: Date.now(),
			pluginId,
			candidateCount: candidates.length,
			caseCount: cases.length,
			recall: ratio(truePos, positives),
			specificity: ratio(trueNeg, negatives),
			results,
		},
	};
}

/**
 * Map an outcome onto gate findings.
 *
 * `not-run` is `info`, never a pass and never a failure: G4 is opt-in and needs a
 * model, so a machine without one must not be blocked from publishing — but the
 * record has to say the check did not happen rather than leaving a silence that
 * reads like a green.
 */
export function triggerFindings(outcome: TriggerEvalOutcome): GateFinding[] {
	if (outcome.status === "not-run") {
		return [{ gate: "G4", severity: "info", message: `G4 trigger eval did not run — ${outcome.reason}` }];
	}
	const { recall, specificity, caseCount } = outcome.record;
	const findings: GateFinding[] = [];
	const pct = (v: number) => `${Math.round(v * 100)}%`;

	if (recall !== undefined && recall < MIN_RECALL) {
		findings.push({
			gate: "G4",
			severity: "error",
			message:
				`Skills fired on only ${pct(recall)} of the prompts they are for (need ${pct(MIN_RECALL)}). ` +
				"Rewrite the description around the situation that should trigger it, not the mechanism.",
		});
	}
	if (specificity !== undefined && specificity < MIN_SPECIFICITY) {
		findings.push({
			gate: "G4",
			severity: "warning",
			message:
				`Skills also fired on ${pct(1 - specificity)} of the prompts they should have stayed quiet on ` +
				`(need ${pct(MIN_SPECIFICITY)} specificity). An over-broad description costs context on every request.`,
		});
	}
	if (findings.length === 0) {
		const parts = [
			recall !== undefined ? `recall ${pct(recall)}` : undefined,
			specificity !== undefined ? `specificity ${pct(specificity)}` : undefined,
		].filter(Boolean);
		findings.push({
			gate: "G4",
			severity: "info",
			message: `G4 trigger eval passed over ${caseCount} case(s): ${parts.join(", ")}.`,
		});
	}
	return findings;
}
